import type * as React from 'react';
import type { CellState, AvailabilityPeriod, CellSelection } from '../lib/availability-types';

/**
 * Shared Radix `PopoverContent` overflow guard: bounds a popover's content to
 * whatever height Radix's own collision detection computed as available, then
 * scrolls internally instead of clipping — Radix's collision SHIFTING alone
 * cannot fix content taller than the viewport (e.g. the queue-join panel's
 * mandatory shared-expiry control, task 4.3,
 * `reservation-mixed-range-and-target-expiry`). Reused by `InspectPopoverCell`,
 * `AvailabilityRichCell`'s INSPECT popover, and `UnifiedSelectPopover` — single
 * source of truth so all three stay in sync.
 */
export const POPOVER_CONTENT_CLAMP_CLASS =
  'max-h-[var(--radix-popover-content-available-height)] overflow-y-auto';

export const POPOVER_CONTENT_WIDTH_CLASS =
  'w-max min-w-[min(calc(var(--spacing)*72),var(--radix-popover-content-available-width))] max-w-[var(--radix-popover-content-available-width)]';

export interface CellPopoverLabels {
  reserveMulti: (count: number, unitLabel: string, periods: string) => string;
  reserveSingle: (period: string, unitLabel: string) => string;
  releaseLabel: string;
  occupiedByOther: React.ReactNode;
  /**
   * Message for a read-only UNAVAILABLE-window cell popover — receives the
   * clicked period's label (e.g. "Maj 2026"). Falls back to occupiedByOther
   * when omitted (callers that never surface availability-window cells).
   */
  unavailableWindowMessage?: (period: string) => string;
  autoSnapWarning: (mediaTypeLabel: string, snappedLabel: string) => string;
  addOptionBtn: string;
  reserveBtn: string;
  upgradeHardBtn?: string;
  closeSaleBtn?: string;
  releaseBtn: string;
  cancelBtn: string;
  closeBtn: string;
  ttlCountdownLabel: string;
  /** Label for the "Join queue" button (e.g. "Dołącz do kolejki"). */
  queueJoinBtn?: string;
  /** Label for the "Leave queue" button (e.g. "Opuść kolejkę"). */
  queueLeaveBtn?: string;
  /** Returns the queue count label given the number of people already waiting. */
  queueCountLabel?: (n: number) => string;
  /** Returns the user's position label when they are already in the queue. */
  queueMyPositionLabel?: (pos: number) => string;
}

export interface CellPopoverState {
  isOwn: boolean;
  showReserveActions: boolean;
  showReleaseActions: boolean;
  showUpgradeHardAction: boolean;
  showCloseSaleAction: boolean;
  showCloseAction: boolean;
  message: React.ReactNode;
}

type CellPopoverMode =
  | 'reserve-multi'
  | 'reserve-single'
  | 'release'
  | 'occupied'
  | 'unavailable-window';

const RESERVE_ACTION_MODES = new Set<CellPopoverMode>(['reserve-multi', 'reserve-single']);

function getPeriodLabels(periodIds: string[], periods: AvailabilityPeriod[]): string[] {
  return periodIds
    .map((periodId) => periods.find((period) => period.id === periodId)?.label ?? periodId)
    .filter(Boolean);
}

function getCellPopoverMode(
  selection: CellSelection,
  getCellStateForPeriod: (periodId: string) => CellState,
): CellPopoverMode {
  if (selection.autoSnap !== undefined || selection.periodIds.length > 1) {
    return 'reserve-multi';
  }

  const firstCellState = getCellStateForPeriod(selection.periodIds[0] ?? '');

  if (firstCellState === 'free' || firstCellState === 'unknown') {
    return 'reserve-single';
  }

  if (
    firstCellState === 'reserved-own-soft' ||
    firstCellState === 'reserved-own-hard' ||
    firstCellState === 'option-own'
  ) {
    return 'release';
  }

  if (firstCellState === 'unavailable') {
    return 'unavailable-window';
  }

  return 'occupied';
}

function getCellPopoverMessage(
  mode: CellPopoverMode,
  selection: CellSelection,
  unitLabel: string,
  labels: CellPopoverLabels,
  periodLabels: string[],
): React.ReactNode {
  switch (mode) {
    case 'reserve-multi':
      return labels.reserveMulti(selection.periodIds.length, unitLabel, periodLabels.join(', '));
    case 'reserve-single':
      return labels.reserveSingle(periodLabels[0] ?? '', unitLabel);
    case 'release':
      return labels.releaseLabel;
    case 'unavailable-window':
      return labels.unavailableWindowMessage
        ? labels.unavailableWindowMessage(periodLabels[0] ?? '')
        : labels.occupiedByOther;
    case 'occupied':
      return labels.occupiedByOther;
  }
}

export function getCellPopoverState(
  selection: CellSelection,
  periods: AvailabilityPeriod[],
  unitLabel: string,
  labels: CellPopoverLabels,
  getCellStateForPeriod: (periodId: string) => CellState,
  getReservationStatusForPeriod?: (
    periodId: string,
  ) => 'SOFT_RESERVATION' | 'HARD_RESERVATION' | null,
): CellPopoverState {
  const periodLabels = getPeriodLabels(selection.periodIds, periods);
  const mode = getCellPopoverMode(selection, getCellStateForPeriod);
  const isOwn = mode === 'release';
  const reservationStatus = getReservationStatusForPeriod?.(selection.periodIds[0] ?? '') ?? null;

  return {
    isOwn,
    showReserveActions: RESERVE_ACTION_MODES.has(mode),
    showReleaseActions: isOwn,
    showUpgradeHardAction: isOwn && reservationStatus === 'SOFT_RESERVATION',
    showCloseSaleAction: isOwn && reservationStatus !== null,
    showCloseAction: mode === 'occupied' || mode === 'unavailable-window',
    message: getCellPopoverMessage(mode, selection, unitLabel, labels, periodLabels),
  };
}
